import React from 'react';
import { Button, Radio, Tag } from 'antd';
import { CarOutlined, CheckCircleFilled, InboxOutlined, NodeIndexOutlined, ShopOutlined, UserOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import type { OrderDeliveryMode } from '../../../transports/transportDirection';
import type { OrderStepContext } from '../orderStepContext';
import { Callout, InfoList, StepSection } from '../OrderProcessParts';
import { OrderLinesSummaryTable } from '../OrderProcessTables';
import OrderTransportSummary, { activeOrderTransports, outgoingOrderTransports } from '../OrderTransportSummary';
import {
  TRANSPORT_STATUS_DISPLAY_COLORS,
  getTransportStatusDisplay,
  getTransportStatusDisplayTranslationKey,
} from '../../../transports/transportRouteViews';
import { useDeliverySummary } from './FulfillmentStep';

/**
 * Gotowe do wydania (makieta `sGotowe`): everything leaves through the
 * warehouse (FIFO) unless the Handlowiec chose „prosto z fabryki”. Three choices —
 * outgoing transport (`deliveryMode` OUR_DELIVERY + a „Magazyn → klient” truck
 * via the #267 dialog), customer self-pickup, or straight from the factory
 * (`Order.fulfillmentSource` FACTORY_DIRECT, the same choice as in the
 * confirmation window: the whole quantity is ordered from the supplier, no
 * outgoing truck from the warehouse).
 */
export default function ReadyStep({
  ctx,
  onDeliveryModeChange,
  onFactoryDirect,
  onOpenTransportLink,
  savingDeliveryMode,
}: {
  ctx: OrderStepContext;
  onDeliveryModeChange: (mode: OrderDeliveryMode) => void;
  /** Switches the order to „prosto z fabryki” (`fulfillmentSource`). */
  onFactoryDirect: () => void;
  onOpenTransportLink: () => void;
  savingDeliveryMode: boolean;
}) {
  const { t } = useTranslation(['orders']);
  const { order, caps, readOnly, facts } = ctx;
  const factory = order.fulfillmentSource === 'FACTORY_DIRECT';
  const factoryTransport = useDeliverySummary(ctx);
  const mode: OrderDeliveryMode = order.deliveryMode === 'CUSTOMER_PICKUP' ? 'CUSTOMER_PICKUP' : 'OUR_DELIVERY';
  const locked = readOnly || !caps.canEditDeliveryMode || savingDeliveryMode;
  const outgoing = outgoingOrderTransports(ctx.transports);
  const linked = activeOrderTransports(ctx.transports);
  const shown = outgoing.length ? outgoing : linked;

  // The saved choice is the selected option from the first render; the selected card says so in words, not only by a radio dot.
  const option = (
    testId: string,
    selected: boolean,
    onSelect: () => void,
    icon: React.ReactNode,
    title: string,
    description: string,
    badge?: React.ReactNode,
  ) => (
    <label className={`zk-option${selected ? ' is-selected' : ''}`} data-testid={testId} data-selected={selected ? 'true' : 'false'}>
      <Radio checked={selected} disabled={locked} onChange={onSelect} aria-label={title} />
      <span className="zk-option__icon" aria-hidden="true">{icon}</span>
      <span className="zk-option__copy">
        <strong>{title}</strong>
        <span>{description}</span>
        {(badge || selected) && (
          <div className="zk-option__badges">
            {selected && <Tag color="blue" icon={<CheckCircleFilled />}>{t('orders:process.ready.selected')}</Tag>}
            {badge}
          </div>
        )}
      </span>
    </label>
  );

  // Statuses on the right: where the goods come from, the transport (also its status, no freight), the goods on hand.
  const draftTrucks = ctx.stockStatus?.supplierDraft.transports ?? [];
  const truckStatuses = (factory ? draftTrucks.map((truck) => truck.status) : shown.map((truck) => truck.status)).filter(Boolean);

  return (
    <div className="zk-stack" data-testid="zk-step-READY">
      <StepSection title={t('orders:process.ready.linesTitle')}>
        <OrderLinesSummaryTable order={order} canViewSalePrices={caps.canViewSalePrices} />
      </StepSection>
      <div className="zk-grid">
        <div className="zk-col">
          <StepSection title={t('orders:process.ready.transportTitle')} icon={<CarOutlined />} testId="zk-transport-choice">
            <div className="zk-options" role="radiogroup" aria-label={t('orders:process.ready.transportTitle')}>
              {option('zk-delivery-OUR_DELIVERY', !factory && mode === 'OUR_DELIVERY', () => onDeliveryModeChange('OUR_DELIVERY'),
                <CarOutlined />, t('orders:process.ready.outgoing'), t('orders:process.ready.outgoingHint'))}
              {option('zk-delivery-CUSTOMER_PICKUP', !factory && mode === 'CUSTOMER_PICKUP', () => onDeliveryModeChange('CUSTOMER_PICKUP'),
                <UserOutlined />, t('orders:process.ready.pickup'), t('orders:process.ready.pickupHint'),
                <Tag color="green">{t('orders:process.ready.pickupBadge')}</Tag>)}
              {option('zk-delivery-FACTORY', factory, onFactoryDirect,
                <NodeIndexOutlined />, t('orders:process.ready.factory'), t('orders:process.ready.factoryHint'),
                <Tag>{t('orders:process.ready.factoryBadge')}</Tag>)}
            </div>
            {!caps.canEditDeliveryMode && !readOnly && <Callout tone="muted">{t('orders:process.ready.modeReadOnly')}</Callout>}
          </StepSection>
          {!factory && mode === 'OUR_DELIVERY' && caps.canViewTransports && (
            <StepSection
              title={t('orders:process.ready.orderTitle')}
              aside={!readOnly && caps.canManageTransports && (
                <Button size="small" icon={<CarOutlined />} onClick={onOpenTransportLink}>
                  {outgoing.length ? t('orders:process.ready.addTransport') : t('orders:process.ready.planTransport')}
                </Button>
              )}
            >
              {shown.length
                ? <OrderTransportSummary transports={shown} canViewCosts={false} hideCost />
                : <Callout tone="warning" flush>{t('orders:process.ready.noTransport')}</Callout>}
            </StepSection>
          )}
          {/* Prod ZK 6/09/2026 (2026-10-22): the ZD rode a factory truck, the ZK
              did not, and the card had no way to add it. The ZK joins the
              truck here („Dołóż do istniejącego auta”). */}
          {factory && caps.canViewTransports && (
            <StepSection
              title={t('orders:process.ready.orderTitle')}
              testId="zk-factory-transport"
              aside={!readOnly && caps.canManageTransports && (
                <Button size="small" icon={<CarOutlined />} onClick={onOpenTransportLink} data-testid="zk-factory-transport-attach">
                  {t('orders:process.ready.attachFactoryTruck')}
                </Button>
              )}
            >
              {linked.length
                ? <OrderTransportSummary transports={linked} canViewCosts={false} hideCost />
                : <Callout tone="warning" flush>{t('orders:process.ready.noFactoryTransport')}</Callout>}
            </StepSection>
          )}
        </div>
        <div className="zk-col">
          <StepSection title={t('orders:process.ready.statusTitle')} icon={<InboxOutlined />} testId="zk-ready-statuses">
            <InfoList rows={[
              [<><InboxOutlined /> {t('orders:process.ready.statusSource')}</>, t(factory ? 'orders:process.ready.statusSourceFactory' : 'orders:process.fulfillment.sourceWarehouse')],
              [<><CarOutlined /> {t('orders:process.ready.statusTransport')}</>, factoryTransport],
              ...(truckStatuses.length ? [[<><CarOutlined /> {t('orders:process.transport.status')}</>, (
                <span className="zk-tags">
                  {truckStatuses.map((status, index) => (
                    <Tag key={`${status}-${index}`} color={TRANSPORT_STATUS_DISPLAY_COLORS[getTransportStatusDisplay(status)]}>
                      {t(getTransportStatusDisplayTranslationKey(status))}
                    </Tag>
                  ))}
                </span>
              )] as [React.ReactNode, React.ReactNode]] : []),
              [<><ShopOutlined /> {t('orders:process.ready.goods')}</>, facts.missing.length
                ? <Tag color="orange">{t('orders:process.ready.goodsMissing', { count: facts.missing.length })}</Tag>
                : <Tag color="green">{t('orders:process.ready.goodsAvailable')}</Tag>],
            ]} />
          </StepSection>
        </div>
      </div>
    </div>
  );
}
